import React, { useRef, useEffect, useReducer, useContext } from 'react';
import PropTypes from 'prop-types';
import fetch from 'unfetch';
import join from 'url-join';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var useInterval = function useInterval(handler, delay) {
  var savedHandler = useRef(); // Update ref.current value if handler changes. This allows our effect below
  // to always get latest handler without us needing to pass it in effect
  // dependencies array and potentially cause effect to re-run every render.

  useEffect(function () {
    savedHandler.current = handler;
  }, [handler]);
  useEffect(function () {
    // Run the handler first, otherwise it won't execute until after the first
    // dely is up.
    savedHandler.current(); // If no delay is given, do not set up the interval. Useful when the component
    // is remounted often and a user prefers to re-fetch data on mount only.

    if (!delay) {
      return undefined;
    }

    var tick = function tick() {
      savedHandler.current();
    };

    var id = setInterval(tick, delay);
    return function () {
      return clearInterval(id);
    };
  }, [delay]);
};

var _process$env = process.env,
    ECHO_APP_ID = _process$env.ECHO_APP_ID,
    ECHO_APP_SECRET = _process$env.ECHO_APP_SECRET,
    ECHO_PRODUCT = _process$env.ECHO_PRODUCT,
    REACT_APP_ECHO_APP_ID = _process$env.REACT_APP_ECHO_APP_ID,
    REACT_APP_ECHO_APP_SECRET = _process$env.REACT_APP_ECHO_APP_SECRET,
    REACT_APP_ECHO_PRODUCT = _process$env.REACT_APP_ECHO_PRODUCT;
var APP_ID = ECHO_APP_ID || REACT_APP_ECHO_APP_ID;
var APP_SECRET = ECHO_APP_SECRET || REACT_APP_ECHO_APP_SECRET;
var PRODUCT = ECHO_PRODUCT || REACT_APP_ECHO_PRODUCT;

if (!APP_ID) {
  throw new Error('@storable/echo requires the `ECHO_APP_ID` or `REACT_APP_ECHO_APP_ID` environment variable to be set.');
}

if (!APP_SECRET) {
  throw new Error('@storable/echo requires the `ECHO_APP_SECRET` or `REACT_APP_ECHO_APP_SECRET` environment variable to be set.');
}

if (!PRODUCT) {
  throw new Error('@storable/echo requires the `ECHO_PRODUCT` or `REACT_APP_ECHO_PRODUCT` environment variable to be set.');
}

var _process$env$1 = process.env,
    ECHO_API_URL = _process$env$1.ECHO_API_URL,
    ECHO_API_VERSION = _process$env$1.ECHO_API_VERSION,
    REACT_APP_ECHO_API_URL = _process$env$1.REACT_APP_ECHO_API_URL,
    REACT_APP_ECHO_API_VERSION = _process$env$1.REACT_APP_ECHO_API_VERSION;
var API_URL = ECHO_API_URL || REACT_APP_ECHO_API_URL;
var API_VERSION = ECHO_API_VERSION || REACT_APP_ECHO_API_VERSION;

if (!API_URL) {
  throw new Error('@storable/echo requires the `ECHO_API_URL` or `REACT_APP_ECHO_API_URL` environment variable to be set.');
}

if (!API_VERSION) {
  throw new Error('@storable/echo requires the `ECHO_API_VERSION` or `REACT_APP_ECHO_API_VERSION` environment variable to be set.');
}

var URL = join(API_URL, API_VERSION);
var NOTIFICATIONS_URL = join(URL, '/notifications');
var INBOX_URL = join(URL, '/inbox');

var buildQuery = function buildQuery(params) {
  var queryParams = Object.entries(params).reduce(function (queries, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];

    if (!value) {
      return queries;
    } // Converts pascalCase to snake_case.
    // fooBarBaz => foo_bar_baz
    // fooBARBaz => foo_bar_baz


    var formattedKey = key.replace(/([A-Z]+)/g, function (x, y) {
      return "_".concat(y.toLowerCase());
    }).replace(/^_/, '');
    return [].concat(_toConsumableArray(queries), ["".concat(encodeURIComponent(formattedKey), "=").concat(encodeURIComponent(value))]);
  }, []);
  return "?".concat(queryParams.join('&'));
};

var ApiClient = /*#__PURE__*/function () {
  function ApiClient(_ref) {
    var userId = _ref.userId,
        rest = _objectWithoutProperties(_ref, ["userId"]);

    _classCallCheck(this, ApiClient);

    this.payload = {
      app_id: APP_ID,
      app_secret: APP_SECRET,
      user_id: userId
    };
    this.query = buildQuery(_objectSpread2(_objectSpread2(_objectSpread2({}, rest), this.payload), {}, {
      product: PRODUCT
    }));
    this.notifications = this.notifications.bind(this);
    this.inbox = this.inbox.bind(this);
    this.acknowledge = this.acknowledge.bind(this);
  }

  _createClass(ApiClient, [{
    key: "notifications",
    value: function notifications() {
      var _this = this;

      var url = join(NOTIFICATIONS_URL, this.query);
      return fetch(url).then(function (res) {
        return res.json();
      }).then(function (data) {
        var _ref2 = data || {},
            notifications = _ref2.notifications;

        if (!notifications) {
          return [];
        }

        return notifications.map(function (notification) {
          return _objectSpread2(_objectSpread2({}, notification), {}, {
            acknowledge: function acknowledge() {
              return _this.acknowledge(notification.id);
            }
          });
        });
      });
    }
  }, {
    key: "inbox",
    value: function inbox() {
      var url = join(INBOX_URL, this.query);
      return fetch(url).then(function (res) {
        return res.json();
      }).then(function (data) {
        var _ref3 = data || {},
            notifications = _ref3.notifications;

        if (!notifications) {
          return [];
        }

        return notifications;
      });
    }
  }, {
    key: "acknowledge",
    value: function acknowledge(id) {
      var url = join(NOTIFICATIONS_URL, String(id));
      var options = {
        method: 'PUT',
        body: JSON.stringify(this.payload),
        headers: {
          'Content-Type': 'application/json'
        }
      };
      return fetch(url, options).then(function (res) {
        return res.json();
      });
    }
  }]);

  return ApiClient;
}();

var DISMISS = '@storable/echo/dismiss';
var FETCH_NOTIFICATIONS = '@storable/echo/fetch/notifications';
var FETCH_INBOX = '@storable/echo/fetch/inbox';
var FETCH_NOTIFICATIONS_SUCCESS = '@storable/echo/fetch/notifications/success';
var FETCH_INBOX_SUCCESS = '@storable/echo/fetch/inbox/success';
var FETCH_NOTIFICATIONS_FAIL = '@storable/echo/fetch/notifications/fail';
var FETCH_INBOX_FAIL = '@storable/echo/fetch/inbox/fail';
var initialState = {
  notifications: [],
  inbox: [],
  notificationsError: null,
  inboxError: null,
  notificationsLoading: false,
  notificationsLoaded: false,
  inboxLoading: false,
  inboxLoaded: false
};

var reducer = function reducer(state, action) {
  switch (action.type) {
    case DISMISS:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        notifications: state.notifications.map(function (notification) {
          return notification.id === action.id ? _objectSpread2(_objectSpread2({}, notification), {}, {
            dismissed: true
          }) : notification;
        })
      });

    case FETCH_NOTIFICATIONS:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        notificationsLoading: true,
        notificationsLoaded: false
      });

    case FETCH_INBOX:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        inboxLoading: true,
        inboxLoaded: false
      });
    // When new notifications are fetched, it's important that we add them to the
    // existing notifications instead of overwriting the old notifications with
    // the new ones. If a new batch of notifications were to come in and suddenly
    // all the existing notifications disappeared, a user would miss out on any
    // unseen notifications. Even worse, they could be in the middle of reading
    // one and have it suddenly be gone from their screen!

    case FETCH_NOTIFICATIONS_SUCCESS:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        notifications: [].concat(_toConsumableArray(state.notifications), _toConsumableArray(action.notifications)),
        notificationsLoading: false,
        notificationsLoaded: true
      });

    case FETCH_INBOX_SUCCESS:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        inbox: _toConsumableArray(action.inbox),
        inboxLoading: false,
        inboxLoaded: true
      });

    case FETCH_NOTIFICATIONS_FAIL:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        notificationsError: action.error,
        notificationsLoading: false,
        notificationsLoaded: false
      });

    case FETCH_INBOX_FAIL:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        inboxError: action.error,
        inboxLoading: false,
        inboxLoaded: false
      });

    default:
      return state;
  }
};

var EchoContext = /*#__PURE__*/React.createContext(initialState);

var propTypes = {
  children: PropTypes.node,
  poll: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  userId: PropTypes.string.isRequired
};
var defaultProps = {
  children: undefined,
  poll: 300000
};

var Echo = function Echo(_ref) {
  var children = _ref.children,
      poll = _ref.poll,
      userId = _ref.userId,
      rest = _objectWithoutProperties(_ref, ["children", "poll", "userId"]);

  var _useReducer = useReducer(reducer, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var client = new ApiClient(_objectSpread2({
    userId: userId
  }, rest));
  useInterval(function () {
    dispatch({
      type: FETCH_NOTIFICATIONS
    });
    client.notifications().then(function (notifications) {
      var formattedNotifications = notifications.map(function (notification) {
        return _objectSpread2(_objectSpread2({}, notification), {}, {
          dismiss: function dismiss() {
            return dispatch({
              type: DISMISS,
              id: notification.id
            });
          }
        });
      });
      dispatch({
        type: FETCH_NOTIFICATIONS_SUCCESS,
        notifications: formattedNotifications
      });
    }).catch(function (error) {
      dispatch({
        type: FETCH_NOTIFICATIONS_FAIL,
        error: error
      });
    });
  }, poll);
  useEffect(function () {
    dispatch({
      type: FETCH_INBOX
    });
    client.inbox().then(function (inbox) {
      dispatch({
        type: FETCH_INBOX_SUCCESS,
        inbox: inbox
      });
    }).catch(function (error) {
      dispatch({
        type: FETCH_INBOX_FAIL,
        error: error
      });
    }); // JSON.stringify needed here so that notifications array is compared with deep equality rather
    // than reference equality so that effect isn't fired every time inbox notifications get polled.
  }, [JSON.stringify(state.notifications)]);
  return /*#__PURE__*/React.createElement(EchoContext.Provider, {
    value: state
  }, children);
};

Echo.propTypes = propTypes;
Echo.defaultProps = defaultProps;

var useEcho = function useEcho() {
  return useContext(EchoContext);
};

export { Echo, useEcho };
